/**
 * Investment Item Cell Factory
 *
 * Factory function to create cell configurations for investment item rows.
 * Similar pattern to helpdeskCellFactory.tsx but for investment items.
 */

import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, Trash, Buildings, Cube, Tag, Wall, CheckCircle, Circle, ChartPie, Wallet, Flag, Lock } from "@phosphor-icons/react";
import { Avatar, Tooltip } from "@heroui/react";
import type { CellConfig } from "shared/components/UniversalTable/components";
import { TABLE_DESIGN } from "shared/components/UniversalTable/constants/tableDesign";
import {
  InvestmentItem,
  InvestmentItemStatus,
  INVESTMENT_STATUS_LABELS,
} from "../types/investment";
import { getUserImageUrlById, getUserName } from "shared/utils/users";

// Helper to get status badge color
const getStatusColor = (status: InvestmentItemStatus): string => {
  switch (status) {
    case InvestmentItemStatus.ONTWERP:
      return "bg-gray-100 text-gray-700 border-gray-200";
    case InvestmentItemStatus.IN_BEOORDELING:
      return "bg-blue-100 text-blue-700 border-blue-200";
    case InvestmentItemStatus.GOEDGEKEURD:
      return "bg-green-100 text-green-700 border-green-200";
    case InvestmentItemStatus.AFGEWEZEN:
      return "bg-red-100 text-red-700 border-red-200";
    case InvestmentItemStatus.IN_PROJECT:
      return "bg-moss-primary/10 text-moss-primary border-moss-primary/30";
    case InvestmentItemStatus.CLOSED:
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

// Helper to get label type badge color
const getLabelTypeColor = (type: string | undefined) => {
  switch (type?.toLowerCase()) {
    case "esthetisch":
      return "bg-pink-100 text-pink-700";
    case "energetisch":
      return "bg-green-100 text-green-700";
    case "veiligheid":
      return "bg-red-100 text-red-700";
    case "comfort":
      return "bg-blue-100 text-blue-700";
    case "technisch":
      return "bg-orange-100 text-orange-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export interface InvestmentCellFactoryProps {
  item: InvestmentItem;
  context: {
    isSelected: boolean;
    isBulkActive: boolean;
    onToggleSelect: (selected: boolean) => void;
    onClearSelection: () => void;
  };
  handlers: {
    onRowClick?: (item: InvestmentItem) => void;
    onEditClick: (item: InvestmentItem) => void;
    onDeleteClick: (item: InvestmentItem) => void;
    /**
     * Mark a line as finished. Only available when the line is IN_PROJECT.
     * Triggers a status change to CLOSED on the backend, which releases any
     * unspent remainder back to the line's bucket as a RELEASE transaction.
     */
    onCompleteClick?: (item: InvestmentItem) => void;
    /**
     * Release this budget detail line. The unspent remainder goes back to the
     * bucket and the line becomes locked (read-only, excluded from project
     * parts). Historical usage % stays visible for stats.
     */
    onReleaseClick?: (item: InvestmentItem) => void;
  };
  users: any[];
}

/**
 * Factory function to create cell configurations for investment item rows
 */
export const createInvestmentItemCells = ({
  item,
  context,
  handlers,
  users,
}: InvestmentCellFactoryProps): CellConfig[] => {
  return [
    // 1. Checkbox Column
    {
      id: "checkbox",
      colSpan: 1,
      align: "center",
      content: (
        <Checkbox
          checked={context.isSelected}
          onCheckedChange={(checked) => {
            context.onToggleSelect(checked === true);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },

    // 2. Actions Column
    {
      id: "actions",
      colSpan: 1,
      align: "center",
      content: item.isReleased ? (
        // Released lines are locked: edit/delete/release all hidden, just a
        // single visual cue that mutations aren't possible anymore.
        <div className="flex items-center justify-center">
          <span
            className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
            title="Vrijgegeven — budget terug naar strategisch budget, lijn is alleen-lezen"
          >
            <Lock className="h-3 w-3" weight="fill" />
            Geblokkeerd
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlers.onEditClick(item);
            }}
            className="shrink-0 rounded-md bg-blue-100 p-1 text-xs text-blue-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-blue-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-blue-300 active:scale-95"
            title="Bewerk item"
          >
            <Pencil className="h-3 w-3" />
          </button>
          {/* Complete button — only shown for IN_PROJECT lines so PMs can
              finalize a line that's done. The status change releases the
              unspent remainder back to its bucket. */}
          {item.status === InvestmentItemStatus.IN_PROJECT &&
            handlers.onCompleteClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onCompleteClick?.(item);
                }}
                className="shrink-0 rounded-md bg-emerald-100 p-1 text-xs text-emerald-700 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-emerald-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-emerald-300 active:scale-95"
                title="Investeringslijn afsluiten"
              >
                <Flag className="h-3 w-3" weight="fill" />
              </button>
            )}
          {/* Release button — hand the unused budget back to the bucket and
              lock the line. Available on every active line so users can park
              a budget detail they no longer need without losing its stats. */}
          {handlers.onReleaseClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.onReleaseClick?.(item);
              }}
              className="shrink-0 rounded-md bg-amber-100 p-1 text-xs text-amber-700 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-amber-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-amber-300 active:scale-95"
              title="Vrijgeven & vergrendelen — budget terug naar strategisch budget"
            >
              <Lock className="h-3 w-3" weight="fill" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlers.onDeleteClick(item);
            }}
            className="shrink-0 rounded-md bg-gray-100 p-1 text-xs text-gray-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-red-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-red-300 active:scale-95"
            title="Verwijder item"
          >
            <Trash className="h-3 w-3" />
          </button>
        </div>
      ),
    },

    // 4. Status Column
    {
      id: "status",
      colSpan: 1,
      align: "center",
      content: item.isReleased ? (
        <div className="flex flex-col items-center gap-1">
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
            title={
              item.releasedAt
                ? `Vrijgegeven op ${new Date(item.releasedAt).toLocaleDateString("nl-BE")}`
                : "Vrijgegeven"
            }
          >
            <Lock className="h-3 w-3" weight="fill" />
            Vrijgegeven
          </span>
          <span className="text-[10px] text-gray-400 line-through">
            {INVESTMENT_STATUS_LABELS[item.status]}
          </span>
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Dispatch custom event for status change - handled by ChangeWithReasonModal
            const ev = new CustomEvent("investment-status-change", {
              detail: {
                itemId: item.id,
                item: item,
              },
            });
            window.dispatchEvent(ev);
          }}
          disabled={item.status === InvestmentItemStatus.IN_PROJECT}
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-opacity ${getStatusColor(item.status)} ${
            item.status === InvestmentItemStatus.IN_PROJECT
              ? "cursor-not-allowed opacity-70"
              : "cursor-pointer hover:opacity-80"
          }`}
          title={
            item.status === InvestmentItemStatus.IN_PROJECT
              ? "Status kan niet worden gewijzigd (in project)"
              : "Klik om status te wijzigen"
          }
        >
          {INVESTMENT_STATUS_LABELS[item.status]}
        </button>
      ),
    },

    // 5. Title Column
    {
      id: "title",
      colSpan: 1,
      align: "left",
      content: (() => {
        // Get unique projects from partAssignments (new system)
        const linkedProjects = item.partAssignments?.reduce((acc, assignment) => {
          if (assignment.project?.id && !acc.find(p => p.id === assignment.project.id)) {
            acc.push(assignment.project);
          }
          return acc;
        }, [] as Array<{ id: number; title?: string }>) || [];

        return (
          <div className="flex min-w-0 flex-1 items-start gap-2 text-left">
            {/* Project indicators - show all linked projects */}
            {linkedProjects.length > 0 && (
              <div className="flex flex-wrap gap-1 shrink-0">
                {linkedProjects.slice(0, 2).map((project) => (
                  <Tooltip key={project.id} content={`Ga naar RENO-${project.id}`} placement="top">
                    <a
                      href={`/renovation/${project.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex h-5 shrink-0 items-center gap-1 rounded bg-brick-primary/10 px-1.5 hover:bg-brick-primary/20 transition-colors"
                    >
                      <Wall className="h-3 w-3 text-brick-primary" weight="fill" />
                      <span className="text-[10px] font-medium text-brick-primary">RENO-{project.id}</span>
                    </a>
                  </Tooltip>
                ))}
                {linkedProjects.length > 2 && (
                  <Tooltip content={`+${linkedProjects.length - 2} meer projecten`} placement="top">
                    <span className="flex h-5 items-center px-1.5 rounded bg-brick-primary/10 text-[10px] font-medium text-brick-primary">
                      +{linkedProjects.length - 2}
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.onRowClick?.(item);
              }}
              className="flex min-w-0 flex-1 flex-col hover:bg-gray-50 rounded p-1 -m-1 transition-colors text-left"
            >
              <span className={`${TABLE_DESIGN.cellContent.text.title} truncate hover:text-moss-primary`}>
                {item.title}
              </span>
              {item.description && (
                <span className="text-xs text-gray-500 truncate">
                  {item.description.substring(0, 100)}
                  {item.description.length > 100 ? "..." : ""}
                </span>
              )}
            </button>
          </div>
        );
      })(),
    },

    // 5. Label Column (with category/type display and click handler)
    {
      id: "label",
      colSpan: 1,
      align: "left",
      content: (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const ev = new CustomEvent("helpdesk-open-picker", {
              detail: {
                mode: "label",
                ticketId: item.id,
                bulkSelection: context.isBulkActive,
              },
            });
            window.dispatchEvent(ev);
          }}
          className="flex flex-col items-start gap-0.5 hover:opacity-70"
          title={item.label ? "Wijzig label" : "Selecteer label"}
        >
          {item.label ? (
            <>
              <span
                className={`${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.interactive} ${TABLE_DESIGN.cellContent.badge.compact} bg-brick-primary/10 text-brick-primary`}
              >
                <Tag className="h-3 w-3" />
                <span className="truncate max-w-[120px]">{item.label.name}</span>
              </span>
              {item.label.type && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${getLabelTypeColor(item.label.type)}`}
                >
                  {item.label.type}
                </span>
              )}
            </>
          ) : (
            <span className={`${TABLE_DESIGN.cellContent.button.secondary} ${TABLE_DESIGN.cellContent.button.compact}`}>
              <Tag className="h-3 w-3 text-gray-400" />
              <span className="text-gray-400">-</span>
            </span>
          )}
        </button>
      ),
    },

    // 6. Building Column
    {
      id: "building",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const ev = new CustomEvent("helpdesk-open-picker", {
              detail: {
                mode: "building",
                ticketId: item.id,
                bulkSelection: context.isBulkActive,
              },
            });
            window.dispatchEvent(ev);
          }}
          className={item.building
            ? `${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.interactive} ${TABLE_DESIGN.cellContent.badge.compact} bg-moss-primary/10 text-moss-primary hover:bg-moss-primary hover:text-white`
            : `${TABLE_DESIGN.cellContent.button.secondary} ${TABLE_DESIGN.cellContent.button.compact}`
          }
          title={item.building ? "Wijzig gebouw" : "Selecteer gebouw"}
        >
          <Buildings className={`h-3 w-3 ${item.building ? "" : "text-gray-400"}`} />
          {item.building && <span>{item.building.id}</span>}
        </button>
      ),
    },

    // 7. Units Column (Kostenplaatsen) - Subtle inline display
    {
      id: "units",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <Tooltip
          content={item.units?.map((u) => u.id).join(", ") || "Geen kostenplaatsen"}
          placement="top"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              const ev = new CustomEvent("helpdesk-open-picker", {
                detail: {
                  mode: "unit",
                  ticketId: item.id,
                  bulkSelection: context.isBulkActive,
                },
              });
              window.dispatchEvent(ev);
            }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            title={item.units?.length ? "Wijzig kostenplaatsen" : "Selecteer kostenplaats"}
          >
            <Cube className="h-3 w-3 shrink-0" />
            {item.units && item.units.length > 0 ? (
              <span className="truncate max-w-[80px]">
                {item.units.length === 1
                  ? item.units[0].id
                  : `${item.units.length} units`}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </button>
        </Tooltip>
      ),
    },

    // 8a. Strategisch budget — what the line claims from its bucket, capped at
    // the bucket's `totalAmount`. Lines with est=0 or no bucket show "—".
    {
      id: "strategicBudget",
      colSpan: 1,
      align: "right",
      content: (() => {
        const est = Number(item.estimatedCostExclVat) || 0;
        const potCap = item.bucket?.totalAmount
          ? Number(item.bucket.totalAmount) || 0
          : 0;
        const strategic = item.bucket?.id
          ? Math.min(est, potCap || est)
          : 0;
        const fmtFull = new Intl.NumberFormat("nl-BE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        const fmtCompact = new Intl.NumberFormat("nl-BE", {
          notation: "compact",
          maximumFractionDigits: 1,
        });

        const openDialog = (e: React.MouseEvent) => {
          e.stopPropagation();
          window.dispatchEvent(
            new CustomEvent("investment-bucket-allocation", {
              detail: { itemId: item.id, item },
            }),
          );
        };

        // Empty state: line has no bucket or claims 0 strategic. Still
        // openable so the user can attach a pot via the dialog.
        if (!item.bucket?.id || strategic <= 0) {
          return (
            <button
              type="button"
              onClick={openDialog}
              className="text-xs text-gray-400 hover:text-moss-primary hover:underline"
              title="Koppel een strategisch budget aan deze lijn"
            >
              + Koppel budget
            </button>
          );
        }

        // Released lines snapshot the unspent portion at release time —
        // surface that as a subline so the user sees how much went back
        // to the strategic budget. The "Origineel" amount (`strategic`)
        // stays the headline; the released delta sits underneath.
        const releasedAmount =
          item.isReleased && Number(item.releasedAmount) > 0
            ? Number(item.releasedAmount)
            : 0;

        return (
          <button
            type="button"
            onClick={openDialog}
            className="flex w-full flex-col items-end gap-1 hover:opacity-90"
            title="Wijzig verdeling van strategisch budget"
          >
            <span className="text-sm font-medium text-gray-900">
              {fmtFull.format(strategic)}
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-md border border-moss-primary/30 bg-moss-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-moss-primary"
              title={`${fmtFull.format(strategic)} op ${item.bucket.name}`}
            >
              <Wallet className="h-3 w-3" weight="duotone" />
              <span className="truncate max-w-[110px]">
                {item.bucket.name}
              </span>
              <span className="text-[9px] tabular-nums opacity-80">
                €{fmtCompact.format(strategic)}
              </span>
            </span>
            {releasedAmount > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                title={`${fmtFull.format(releasedAmount)} teruggevloeid naar ${item.bucket.name} bij vrijgeven`}
              >
                <Lock className="h-3 w-3" weight="fill" />
                <span className="text-[9px] tabular-nums">
                  −{fmtFull.format(releasedAmount)} vrijgegeven
                </span>
              </span>
            )}
          </button>
        );
      })(),
    },

    // 9. Allocated Budget Column (Gebruikt in projectonderdelen)
    //
    // Per-assignment breakdown so the user sees exactly which project parts
    // are consuming this line's budget. Each chip:
    //   RENO-{projectId}-O{partId}  →  link to the part detail page
    //   {part.label}                →  category badge
    //   €{assigned}                 →  this part's slice
    {
      id: "allocatedBudget",
      colSpan: 1,
      align: "right",
      content: (() => {
        const allocated = Number(item.allocatedBudget) || 0;
        const released = Number(item.releasedAmount) || 0;
        const fullEst = Number(item.estimatedCostExclVat) || 0;
        // "Initieel voorzien" = what's still in scope for this line: the
        // original estimate minus anything released back to the bucket.
        // Without this, a 5K line with 2K released and 3K assigned would
        // misleadingly read 60% partial instead of fully accounted-for.
        const total = Math.max(0, fullEst - released);
        const percentage =
          total > 0 ? Math.min(100, Math.round((allocated / total) * 100)) : 0;
        const remaining = Math.max(0, total - allocated);
        const isFullyAllocated = total > 0 && allocated >= total;
        const hasAllocation = allocated > 0;

        const fmtCurrency = new Intl.NumberFormat("nl-BE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

        // Group assignments by project-part so the same part never renders
        // twice. Multiple raw assignments to one part get summed into a
        // single chip. Sort by amount desc so the biggest consumer surfaces
        // first.
        type GroupedPart = {
          key: string;
          projectId?: number;
          partId?: number;
          partTitle?: string;
          labelName?: string;
          amount: number;
        };
        const groupedMap = new Map<string, GroupedPart>();
        for (const a of item.partAssignments ?? []) {
          const amt = Number(a.assignedAmount) || 0;
          if (amt <= 0) continue;
          const projectId = a.project?.id;
          const partId = a.part?.id;
          const key =
            projectId && partId
              ? `p:${projectId}:${partId}`
              : projectId
                ? `r:${projectId}`
                : `a:${a.id}`;
          const existing = groupedMap.get(key);
          if (existing) {
            existing.amount += amt;
          } else {
            groupedMap.set(key, {
              key,
              projectId,
              partId,
              partTitle: a.part?.title,
              labelName: a.part?.label?.name,
              amount: amt,
            });
          }
        }
        const assignments = Array.from(groupedMap.values()).sort(
          (a, b) => b.amount - a.amount,
        );

        const getUsageIcon = () => {
          if (isFullyAllocated) {
            return <CheckCircle className="h-4 w-4 text-green-500" weight="fill" />;
          } else if (hasAllocation) {
            return <ChartPie className="h-4 w-4 text-orange-500" weight="fill" />;
          } else {
            return <Circle className="h-4 w-4 text-gray-300" />;
          }
        };

        const getUsageTooltip = () => {
          if (isFullyAllocated) return "Volledig toegewezen";
          if (hasAllocation) return `Gedeeltelijk toegewezen (${percentage}%)`;
          return "Nog niet toegewezen";
        };

        return (
          <Tooltip content={getUsageTooltip()} placement="top">
            <div className="flex items-center gap-2">
              {getUsageIcon()}
              <div className="flex flex-col items-end gap-1">
                {/* Per-assignment chips: id + label + amount, linking to the
                    part detail page. */}
                {assignments.length > 0 && (
                  <div className="flex flex-col items-end gap-0.5">
                    {assignments.map((g) => {
                      const { projectId, partId, labelName, partTitle, amount } = g;
                      const partCode =
                        projectId && partId
                          ? `RENO-${projectId}-O${partId}`
                          : projectId
                            ? `RENO-${projectId}`
                            : null;
                      const href =
                        projectId && partId
                          ? `/renovation/${projectId}/part/${partId}`
                          : projectId
                            ? `/renovation/${projectId}`
                            : null;

                      const inner = (
                        <span className="inline-flex items-center gap-1 rounded-md border border-brick-primary/30 bg-brick-primary/5 px-1.5 py-0.5 text-[10px] font-medium">
                          {partCode && (
                            <span className="font-semibold text-brick-primary">
                              {partCode}
                            </span>
                          )}
                          {labelName && (
                            <span className="rounded-sm bg-white/70 px-1 text-[9px] font-medium text-neutral-secondary-mid">
                              {labelName}
                            </span>
                          )}
                          <span className="tabular-nums text-orange-700">
                            {fmtCurrency.format(amount)}
                          </span>
                        </span>
                      );

                      return href ? (
                        <a
                          key={g.key}
                          href={href}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:opacity-80"
                          title={
                            partTitle
                              ? `${partTitle}${labelName ? ` (${labelName})` : ""}`
                              : labelName
                          }
                        >
                          {inner}
                        </a>
                      ) : (
                        <span key={g.key}>{inner}</span>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <span
                    className={`text-sm font-medium ${
                      isFullyAllocated
                        ? "text-green-600"
                        : hasAllocation
                          ? "text-orange-600"
                          : "text-gray-400"
                    }`}
                  >
                    {fmtCurrency.format(allocated)}
                  </span>
                  <span
                    className={`text-xs ${
                      isFullyAllocated
                        ? "text-green-500"
                        : hasAllocation
                          ? "text-orange-500"
                          : "text-gray-400"
                    }`}
                  >
                    ({percentage}%)
                  </span>
                </div>
                {hasAllocation && !isFullyAllocated && (
                  <span className="text-[10px] text-gray-400">
                    Rest: {fmtCurrency.format(remaining)}
                  </span>
                )}
              </div>
            </div>
          </Tooltip>
        );
      })(),
    },

    // 9b. Overflow / Onvoorzien — purely the excess of project-part spending
    // over the line's strategic intent. If projects haven't used more than
    // what's strategic, overflow is 0 (regardless of how big the line's
    // wishful `est` is).
    {
      id: "overflowBudget",
      colSpan: 1,
      align: "right",
      content: (() => {
        // Source of truth for line-level overflow is now the persisted
        // per-assignment `overflowAmount` summed over partAssignments —
        // matches what the project parts list shows. Falls back to the
        // legacy line-level math when no assignments exist (line not yet
        // linked to any project part).
        const assignments = (item.partAssignments ?? []) as Array<{
          overflowAmount?: number;
        }>;
        const overflow = assignments.length
          ? assignments.reduce(
              (s, a) => s + (Number(a.overflowAmount) || 0),
              0,
            )
          : (() => {
              const est = Number(item.estimatedCostExclVat) || 0;
              const allocated = Number(item.allocatedBudget) || 0;
              const potCap = item.bucket?.totalAmount
                ? Number(item.bucket.totalAmount) || 0
                : 0;
              const strategic = item.bucket?.id
                ? Math.min(est, potCap || est)
                : 0;
              return Math.max(0, allocated - strategic);
            })();
        const fmtFull = new Intl.NumberFormat("nl-BE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

        if (overflow <= 0) {
          return <span className="text-xs text-gray-400">—</span>;
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              window.dispatchEvent(
                new CustomEvent("investment-bucket-allocation", {
                  detail: { itemId: item.id, item },
                }),
              );
            }}
            className="text-sm font-medium text-amber-700 hover:underline"
            title={`${fmtFull.format(overflow)} verwachte overflow`}
          >
            {fmtFull.format(overflow)}
          </button>
        );
      })(),
    },

    // 13. Created By Column
    {
      id: "createdBy",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <Tooltip
          content={getUserName(item.createdByUserId, users).name || "Onbekend"}
          placement="top"
        >
          <div className="flex items-center justify-center">
            <Avatar
              className="h-6 w-6"
              src={getUserImageUrlById(item.createdByUserId, users)}
            />
          </div>
        </Tooltip>
      ),
    },

    // 14. Created At Column
    {
      id: "createdAt",
      colSpan: 1,
      align: "center",
      hidden: true,
      content: (
        <div className="text-neutral-secondary-mid text-[11px]">
          {new Date(item.createdAt).toLocaleDateString("nl-NL")}
        </div>
      ),
    },
  ];
};
